import { Injectable } from '@angular/core';
import { JSONSchema, StorageMap } from '@ngx-pwa/local-storage';
import {
  type IUserWorkspace,
  shareReplayCold,
} from '@principle-theorem/shared';
import { type Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

const WORKSPACE_STORAGE_KEY = 'workspace';

type IStoredWorkspace = Pick<IUserWorkspace, 'uid' | 'workspace' | 'type'>;

interface ISchemaFor<T> {
  type: 'object';
  properties: { [K in keyof Required<T>]: JSONSchema };
  required?: readonly (keyof T & string)[];
}

const storageSchema = {
  type: 'object',
  properties: {
    workspace: {
      type: 'string',
    },
    uid: {
      type: 'string',
    },
    type: {
      type: 'string',
    },
  },
  required: ['workspace', 'uid', 'type'],
} satisfies ISchemaFor<IStoredWorkspace>;

@Injectable({
  providedIn: 'root',
})
export class WorkspaceService {
  private _workspaces$: ReplaySubject<IUserWorkspace[]> = new ReplaySubject(1);
  value$: Observable<IUserWorkspace | undefined>;
  workspace$: Observable<string | undefined>;
  workspaces$: Observable<IUserWorkspace[]>;

  constructor(private _storage: StorageMap) {
    this.value$ = this._storage
      .watch<IUserWorkspace>(WORKSPACE_STORAGE_KEY, storageSchema)
      .pipe(shareReplayCold());

    this.workspace$ = this.value$.pipe(
      map((value) => value?.workspace),
      shareReplayCold()
    );

    this.workspaces$ = this._workspaces$.asObservable().pipe(shareReplayCold());
  }

  async setWorkspace(workspace: IUserWorkspace): Promise<void> {
    const stored: IStoredWorkspace = {
      uid: workspace.uid,
      workspace: workspace.workspace,
      type: workspace.type,
    };
    await this._storage
      .set(WORKSPACE_STORAGE_KEY, stored, storageSchema)
      .toPromise();
  }

  setWorkspaces(workspaces: IUserWorkspace[]): void {
    this._workspaces$.next(workspaces);
  }

  async reset(): Promise<void> {
    this.setWorkspaces([]);
    await this._storage.delete(WORKSPACE_STORAGE_KEY).toPromise();
  }
}
